import type { ProfilePayload } from "./profile";

export interface AuthSession {
  email: string | null;
  nonce: string;
  restUrl: string;
  privacyUrl: string;
  logoUrl: string;
  googleClientId: string;
}

export interface SavedResultSummary {
  id: number;
  createdAt: string;
  careerId: string;
  matchPercent: number;
  printUrl: string;
}

export interface LeadCapturePayload {
  email: string;
  tool: "radar" | "skills-match";
  discipline?: string;
  interest?: string;
  privacyConsent: boolean;
  marketingConsent: boolean;
  sourceUrl: string;
  utm: Record<string, string>;
}

type Listener = (session: AuthSession) => void;

const listeners = new Set<Listener>();

function cfg() {
  return window.BOOSTSCIENCE_OBSERVATORY;
}

function initialSession(): AuthSession {
  const c = cfg();
  return {
    email: c?.loggedIn && c.userEmail ? c.userEmail : null,
    nonce: c?.restNonce ?? "",
    restUrl: c?.restUrl ?? "",
    privacyUrl: c?.privacyUrl ?? "https://boostscience.eu/privacy-policy-2/",
    logoUrl: c?.logoUrl ?? "",
    googleClientId: c?.googleClientId ?? "",
  };
}

let session: AuthSession = initialSession();

export function getGoogleClientId(): string {
  return session.googleClientId || cfg()?.googleClientId || "";
}

export function getSession(): AuthSession {
  return session;
}

export function authEnabled(): boolean {
  return Boolean(session.restUrl);
}

export function subscribeAuth(fn: Listener): () => void {
  listeners.add(fn);
  fn(session);
  return () => listeners.delete(fn);
}

function setSession(next: Partial<AuthSession>): void {
  session = { ...session, ...next };
  for (const fn of listeners) fn(session);
}

interface WpErrorBody {
  code?: string;
  message?: string;
  nonce?: string;
  email?: string | null;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!session.restUrl) {
    throw new Error("Saving is only available on the published Observatory.");
  }
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (session.nonce) headers.set("X-WP-Nonce", session.nonce);
  const res = await fetch(`${session.restUrl}${path.replace(/^\//, "")}`, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  const body = (await res.json().catch(() => ({}))) as T & WpErrorBody;
  if (typeof body.nonce === "string" && body.nonce) {
    setSession({ nonce: body.nonce, email: body.email === undefined ? session.email : body.email });
  }
  if (!res.ok) {
    throw new Error(body.message || "Request failed.");
  }
  return body;
}

export async function refreshSession(): Promise<AuthSession> {
  if (!authEnabled()) return session;
  const data = await api<{ email: string | null; nonce: string }>("me");
  setSession({ email: data.email ?? null, nonce: data.nonce || session.nonce });
  return session;
}

export async function captureLead(payload: LeadCapturePayload): Promise<void> {
  await api<{ ok: boolean; nonce?: string }>("leads", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function registerAccount(email: string, password: string, consent: boolean): Promise<void> {
  const data = await api<{ email: string | null; nonce: string }>("register", {
    method: "POST",
    body: JSON.stringify({ email, password, consent }),
  });
  setSession({ email: data.email ?? email, nonce: data.nonce || session.nonce });
}

export async function loginAccount(email: string, password: string): Promise<void> {
  const data = await api<{ email: string | null; nonce: string }>("login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setSession({ email: data.email ?? email, nonce: data.nonce || session.nonce });
}

export async function loginWithGoogle(idToken: string, consent: boolean): Promise<void> {
  const data = await api<{ email: string | null; nonce: string }>("google", {
    method: "POST",
    body: JSON.stringify({ idToken, consent }),
  });
  setSession({ email: data.email ?? session.email, nonce: data.nonce || session.nonce });
}

export async function logoutAccount(): Promise<void> {
  const data = await api<{ email: string | null; nonce: string }>("logout", { method: "POST" });
  setSession({ email: null, nonce: data.nonce || session.nonce });
}

export async function saveProfile(payload: ProfilePayload, consent: boolean): Promise<SavedResultSummary> {
  return api<SavedResultSummary>("results", {
    method: "POST",
    body: JSON.stringify({ payload, consent }),
  });
}

export async function listResults(): Promise<SavedResultSummary[]> {
  const data = await api<{ results: SavedResultSummary[] }>("results");
  return data.results ?? [];
}
